import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import bowser from 'yola-bowser';
import { DifmPopup } from '@yola/webapp-plugins';
import { dialogs, integration, site, view } from '@yola/ws-sdk';
import yolaDialogs from 'yola-editor/src/js/modules/dialogs';
import contextMenu from 'src/js/modules/context-menu';
import customUi from 'src/js/modules/custom-ui';
import mainMenu from 'src/js/modules/main-menu';
import segment from 'src/js/modules/analytics/segment';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import useOnceWhen from '../../utils/custom-hooks/use-once-when';
import { hasGoldSubscription } from '../verifiers/has-gold-subscription';
import isDifmOrderSubmitted from '../verifiers/is-difm-order-submitted';

const { customUiTypes } = customUi;
const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const CUSTOM_UI_TYPES_TO_HIDE_DIFM_POPUP = [
  customUiTypes.EDIT_BACKGROUND_IMAGE_FOCAL_POINT,
  customUiTypes.EDIT_INLINE_IMAGE_FOCAL_POINT,
];

const DIFM_POPUP = 'DIFM_POPUP';

const shouldShowPopup = (storageKey) => {
  if (!storageKey) return false;

  const nextDateToShow = localStorage.getItem(storageKey);
  if (!nextDateToShow) return true;

  return nextDateToShow <= new Date();
};

const DifmPopupContainer = () => {
  const userId = useSelector(integration.selectors.getUserId);

  const difmPopupKey = userId ? `${DIFM_POPUP}_${userId}` : null;
  const isInitiallyVisible = shouldShowPopup(difmPopupKey);

  const [isVisible, setIsVisible] = useState(isInitiallyVisible);

  const isMobile = bowser.mobile;
  const dispatch = useDispatch();
  const activeYolaDialog = useSelector(yolaDialogs.selectors.getDialog);
  const activeDialog = useSelector(dialogs.selectors.getDialog);
  const isContextMenuVisible = useSelector(contextMenu.selectors.getVisibilityStatus);
  const customUiComponent = useSelector(customUi.selectors.getComponent);
  const { businessCategory } = useSelector(site.selectors.getSettings, shallowEqual);
  const isViewLoaded = useSelector(view.selectors.getLoadedStatus);
  const isMainMenuVisible = useSelector(mainMenu.selectors.getVisibilityStatus);
  const [featureFlags] = useFeatureFlags(['nn_difm_promo']);
  const { nn_difm_promo: nnDifmPromoFeatureFlagEnabled } = featureFlags;

  const isGold = hasGoldSubscription();
  const isOrderSubmitted = isDifmOrderSubmitted();

  const shouldRenderDifmPopup =
    nnDifmPromoFeatureFlagEnabled &&
    !isGold &&
    !isOrderSubmitted &&
    isViewLoaded &&
    isVisible &&
    !isMainMenuVisible &&
    !activeDialog.type &&
    !activeYolaDialog.type &&
    !CUSTOM_UI_TYPES_TO_HIDE_DIFM_POPUP.includes(customUiComponent?.id) &&
    (!isMobile || !isContextMenuVisible);

  const markDifmPopupAsClosed = useCallback(() => {
    const inOneMonth = new Date().setMonth(new Date().getMonth() + 1);

    localStorage.setItem(difmPopupKey, inOneMonth);
  }, [difmPopupKey]);

  const handleClose = useCallback(() => {
    markDifmPopupAsClosed();

    trackEvent(events.DIFM_PROMOTION_CLOSE_CLICKED, {
      businessCategory: businessCategory || null,
    });

    setIsVisible(false);
  }, [businessCategory, markDifmPopupAsClosed]);

  const handleNext = useCallback(() => {
    markDifmPopupAsClosed();

    trackEvent(events.DIFM_PROMOTION_LEARNMORE_CLICKED, {
      businessCategory: businessCategory || null,
    });

    setIsVisible(false);
    dispatch(dialogs.actions.show(dialogTypes.DIFM_UPSELL_DIALOG));
  }, [businessCategory, markDifmPopupAsClosed, dispatch]);

  useOnceWhen(shouldRenderDifmPopup, () => {
    trackEvent(events.DIFM_PROMOTION_DISPLAYED, {
      businessCategory: businessCategory || null,
    });
  });

  useEffect(() => {
    setIsVisible(isInitiallyVisible);
  }, [isInitiallyVisible]);

  if (!shouldRenderDifmPopup) return null;

  return <DifmPopup onNext={handleNext} onClose={handleClose} />;
};

export default DifmPopupContainer;
